import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Download, Database, ShieldAlert, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import authFetch from '../../utils/authFetch';

// HARDCODED FIX: Smartly switch between Localhost and Secure Server
const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';
const EXCLUDED_PREFIXES = ['kn_', 'kpp_', 'np_', 'ev_'];

const boxStyle = {
  backgroundColor: 'white',
  borderRadius: '8px',
  border: '1px solid #e2e8f0',
  overflow: 'hidden',
};

const headerCellStyle = {
  textAlign: 'left',
  padding: '10px 12px',
  fontWeight: 600,
  color: '#64748b',
  fontSize: '12px',
};

const formatPreviewValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const DbBackupPage = () => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingFiltered, setDownloadingFiltered] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [preview, setPreview] = useState({ columns: [], rows: [], totalRows: 0, previewLimit: 100 });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewError, setPreviewError] = useState('');
  const navigate = useNavigate();

  const filteredTables = useMemo(
    () => tables.filter((table) => !EXCLUDED_PREFIXES.some((prefix) => table.table_name.toLowerCase().startsWith(prefix))),
    [tables]
  );

  useEffect(() => {
    const fetchTables = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await authFetch(`${API_BASE}/api/admin/db-tables`);
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || 'Failed to load tables');
        }
        const data = await res.json();
        setTables(data);
      } catch (err) {
        console.error(err);
        setError(err.message || 'Failed to load tables');
      } finally {
        setLoading(false);
      }
    };

    fetchTables();
  }, []);

  useEffect(() => {
    const fetchPreview = async () => {
      if (!selectedTable) {
        setPreview({ columns: [], rows: [], totalRows: 0, previewLimit: 100 });
        setPreviewError('');
        return;
      }

      setPreviewLoading(true);
      setPreviewError('');
      try {
        const params = new URLSearchParams({
          schema: selectedTable.table_schema,
          table: selectedTable.table_name,
          limit: '100',
        });
        const res = await authFetch(`${API_BASE}/api/admin/db-table-preview?${params.toString()}`);
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || 'Failed to load preview');
        }
        const data = await res.json();
        setPreview(data);
      } catch (err) {
        console.error(err);
        setPreviewError(err.message || 'Failed to load preview');
      } finally {
        setPreviewLoading(false);
      }
    };

    fetchPreview();
  }, [selectedTable]);

  const downloadFromUrl = async (url, fallbackName, setBusy) => {
    setBusy(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Download failed');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadTable = async (schema, table) => {
    try {
      const params = new URLSearchParams({ schema, table });
      await downloadFromUrl(`${API_BASE}/api/admin/db-export?${params.toString()}`, `${schema}_${table}.csv`, () => {});
    } catch (err) {
      console.error(err);
      alert(`Failed to download table ${schema}.${table}: ${err.message}`);
    }
  };

  const handleDownloadAll = async () => {
    if (!window.confirm('Download ALL non-system tables as a ZIP backup?')) return;
    try {
      await downloadFromUrl(`${API_BASE}/api/admin/db-export-all`, 'db_backup.zip', setDownloadingAll);
    } catch (err) {
      console.error(err);
      alert(`Failed to download all tables: ${err.message}`);
    }
  };

  const handleDownloadFiltered = async () => {
    if (!window.confirm(`Download all tables except those starting with ${EXCLUDED_PREFIXES.join(', ')}?`)) return;
    try {
      const params = new URLSearchParams({ excludePrefixes: EXCLUDED_PREFIXES.join(',') });
      await downloadFromUrl(`${API_BASE}/api/admin/db-export-all?${params.toString()}`, 'db_backup_filtered.zip', setDownloadingFiltered);
    } catch (err) {
      console.error(err);
      alert(`Failed to download filtered tables: ${err.message}`);
    }
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', paddingBottom: '40px', fontFamily: 'Inter, sans-serif' }}>
      <button
        onClick={() => navigate('/admin')}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          color: '#64748b',
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          fontSize: '14px',
          marginBottom: '10px',
        }}
      >
        <ArrowLeft size={16} /> Back to Admin Dashboard
      </button>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Database size={24} /> Database Backup
          </h1>
          <div style={{ marginTop: '8px', color: '#64748b', fontSize: '13px' }}>
            Click a table row to preview its content. Use the buttons to export one or many tables.
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={handleDownloadFiltered}
            disabled={downloadingFiltered || loading || filteredTables.length === 0}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#0f766e',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 14px',
              cursor: downloadingFiltered || loading || filteredTables.length === 0 ? 'not-allowed' : 'pointer',
              opacity: downloadingFiltered || loading || filteredTables.length === 0 ? 0.6 : 1,
              fontSize: '14px',
            }}
          >
            <Download size={16} /> {downloadingFiltered ? 'Preparing filtered ZIP...' : 'Download Filtered ZIP'}
          </button>
          <button
            onClick={handleDownloadAll}
            disabled={downloadingAll || loading || tables.length === 0}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#1d4ed8',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 14px',
              cursor: downloadingAll || loading || tables.length === 0 ? 'not-allowed' : 'pointer',
              opacity: downloadingAll || loading || tables.length === 0 ? 0.6 : 1,
              fontSize: '14px',
            }}
          >
            <Download size={16} /> {downloadingAll ? 'Preparing ZIP...' : 'Download All as ZIP'}
          </button>
        </div>
      </div>

      <div
        style={{
          marginBottom: '16px',
          padding: '12px 14px',
          borderRadius: '8px',
          backgroundColor: '#f0f9ff',
          border: '1px solid #e0f2fe',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          fontSize: '13px',
          color: '#0f172a',
        }}
      >
        <ShieldAlert size={18} color="#0369a1" />
        <div>
          <strong>Filtered export:</strong> excludes any table whose name starts with {EXCLUDED_PREFIXES.join(', ')}.
          <div style={{ marginTop: '4px', color: '#475569' }}>
            Matching tables in the current list: {filteredTables.length} of {tables.length}
          </div>
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: '16px', padding: '10px 12px', borderRadius: '6px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#7f1d1d', fontSize: '13px' }}>
          {error}
        </div>
      )}

      <div style={{ ...boxStyle, marginBottom: '20px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            <tr>
              <th style={headerCellStyle}>Schema</th>
              <th style={headerCellStyle}>Table</th>
              <th style={headerCellStyle}>Status</th>
              <th style={{ ...headerCellStyle, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="4" style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>Loading tables...</td>
              </tr>
            ) : tables.length === 0 ? (
              <tr>
                <td colSpan="4" style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>No tables found.</td>
              </tr>
            ) : (
              tables.map((table) => {
                const isExcluded = EXCLUDED_PREFIXES.some((prefix) => table.table_name.toLowerCase().startsWith(prefix));
                const isSelected = selectedTable && selectedTable.table_schema === table.table_schema && selectedTable.table_name === table.table_name;

                return (
                  <tr
                    key={`${table.table_schema}.${table.table_name}`}
                    onClick={() => setSelectedTable(table)}
                    style={{
                      borderBottom: '1px solid #f1f5f9',
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                    }}
                  >
                    <td style={{ padding: '8px 12px', color: '#0f172a' }}>{table.table_schema}</td>
                    <td style={{ padding: '8px 12px', color: '#0f172a' }}>{table.table_name}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 8px',
                          borderRadius: '999px',
                          backgroundColor: isExcluded ? '#fff7ed' : '#ecfdf5',
                          color: isExcluded ? '#c2410c' : '#047857',
                          fontSize: '12px',
                          fontWeight: 600,
                        }}
                      >
                        <Eye size={12} /> {isExcluded ? 'Excluded from filtered ZIP' : 'Included in filtered ZIP'}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDownloadTable(table.table_schema, table.table_name);
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          backgroundColor: '#2563eb',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 10px',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        <Download size={14} /> CSV
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div style={boxStyle}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ margin: 0, color: '#0f172a', fontSize: '18px' }}>Table Preview</h2>
            <div style={{ marginTop: '6px', color: '#64748b', fontSize: '13px' }}>
              {selectedTable
                ? `Showing up to ${preview.previewLimit} rows from ${selectedTable.table_schema}.${selectedTable.table_name}`
                : 'Select a table above to inspect its contents.'}
            </div>
          </div>
          {selectedTable && !previewLoading && !previewError && (
            <div style={{ color: '#334155', fontSize: '13px' }}>
              Total rows: <strong>{preview.totalRows}</strong>
            </div>
          )}
        </div>

        {previewError && (
          <div style={{ margin: '16px', padding: '10px 12px', borderRadius: '6px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#7f1d1d', fontSize: '13px' }}>
            {previewError}
          </div>
        )}

        {!selectedTable ? (
          <div style={{ padding: '24px', color: '#94a3b8', textAlign: 'center' }}>No table selected.</div>
        ) : previewLoading ? (
          <div style={{ padding: '24px', color: '#94a3b8', textAlign: 'center' }}>Loading preview...</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <tr>
                  {preview.columns.map((column) => (
                    <th key={column} style={headerCellStyle}>{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.rows.length === 0 ? (
                  <tr>
                    <td colSpan={Math.max(preview.columns.length, 1)} style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>
                      This table has no rows.
                    </td>
                  </tr>
                ) : (
                  preview.rows.map((row, rowIndex) => (
                    <tr key={rowIndex} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      {preview.columns.map((column) => (
                        <td
                          key={`${rowIndex}-${column}`}
                          style={{
                            padding: '8px 12px',
                            color: '#0f172a',
                            maxWidth: '280px',
                            verticalAlign: 'top',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {formatPreviewValue(row[column])}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DbBackupPage;